import React, { useState, useRef, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { track } from '@vercel/analytics';
import { useTranslation } from 'react-i18next';
import Logo from './components/Logo.jsx';
import LanguageSwitcher from './components/LanguageSwitcher.jsx';
import FileUpload from './components/FileUpload.jsx';
import ConversionOptions from './components/ConversionOptions';
import FileList from './components/FileList.jsx';
import ProgressBar from './components/ProgressBar.jsx';
import Footer from './components/Footer.jsx';
import GoogleAds from './components/GoogleAds.jsx';
import ConversionService from './services/ConversionService.js';
import './App.css';

function App() {
  const { t, i18n } = useTranslation();
  const [files, setFiles] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [progress, setProgress] = useState({
    overallProgress: 0,
    currentFile: '',
    currentFileIndex: 0,
    totalFiles: 0,
    fileProgress: 0,
    status: ''
  });
  const [options, setOptions] = useState({
    imageQuality: 0.8,
    maxWidth: 1200,
    maxHeight: 1600,
    optimizeImages: true,
    compressionLevel: 6
  });

  const fileInputRef = useRef(null);
  const conversionService = new ConversionService();

  // Set document direction for RTL languages
  useEffect(() => {
    const isRTL = i18n.language === 'ar';
    document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const handleFilesSelected = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter(file => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.cbz') || fileName.endsWith('.cbr') || fileName.endsWith('.cb7') || fileName.endsWith('.zip') || fileName.endsWith('.docx') || fileName.endsWith('.pdf');
    });
    
    if (validFiles.length === 0) {
      setStatus({ type: 'error', message: 'Please select files with valid type.' });
      return;
    }

    setFiles(prevFiles => [...prevFiles, ...validFiles]);
    setStatus({ type: 'success', message: `Added ${validFiles.length} file(s) successfully!` });
  };

  // Auto-dismiss messages after 3 seconds
  useEffect(() => {
    if (status.type === 'success' || status.type === 'error' || status.type === 'info') {
      const timer = setTimeout(() => {
        setStatus({ type: '', message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status.type]);

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleClearFiles = () => {
    setFiles([]);
    setStatus({ type: '', message: '' });
    setProgress({
      overallProgress: 0,
      currentFile: '',
      currentFileIndex: 0,
      totalFiles: 0,
      fileProgress: 0,
      status: ''
    });
  };

  const handleOptionsChange = (newOptions) => {
    setOptions(newOptions);
  };

  const handleConvert = async () => {
    if (files.length === 0) {
      setStatus({ type: 'error', message: 'Please select files to convert.' });
      return;
    }

    setIsConverting(true);
    setProgress({
      overallProgress: 0,
      currentFile: '',
      currentFileIndex: 0,
      totalFiles: files.length,
      fileProgress: 0,
      status: 'starting'
    });
    // Clear any existing status messages when starting conversion
    setStatus({ type: '', message: '' });

    const startTime = Date.now();

    try {
      await conversionService.convertFiles(files, options, (progressData) => {
        setProgress(progressData);
        
        // Only show status messages for important events, not every progress update
        // The progress bar already shows detailed conversion info
        if (progressData.status === 'zipping') {
          setStatus({ type: 'info', message: 'Generating ZIP file...' });
        } else if (progressData.status === 'finished') {
          setStatus({ type: 'info', message: `Successfully converted ${files.length} file(s)!` });
        }
        // Remove the 'converting' status popup since progress bar shows this info
      });

      const conversionTime = Date.now() - startTime;
      setStatus({ type: 'success', message: `Successfully converted ${files.length} file(s)!` });
      
      // Track successful conversion
      track('conversion_completed', { 
        file_count: files.length,
        conversion_time_ms: conversionTime,
        conversion_time_seconds: Math.round(conversionTime / 1000)
      });
      
    } catch (error) {
      const conversionTime = Date.now() - startTime;
      setStatus({ type: 'error', message: `Conversion failed: ${error.message}` });
      setProgress({
        overallProgress: 0,
        currentFile: '',
        currentFileIndex: 0,
        totalFiles: 0,
        fileProgress: 0,
        status: ''
      });
      
      // Track conversion error
      track('conversion_failed', { 
        file_count: files.length,
        error_message: error.message,
        conversion_time_ms: conversionTime
      });
      
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="App">
      {/* Left Sidebar Ad - Using Auto Ads instead */}
      {/* <GoogleAds 
        adSlot="5534587888"
        className="sidebar-ad left-sidebar-ad"
        adFormat="auto"
        responsive={true}
      /> */}
      
      {/* Right Sidebar Ad - Using Auto Ads instead */}
      {/* <GoogleAds 
        adSlot="5534587888"
        className="sidebar-ad right-sidebar-ad"
        adFormat="auto"
        responsive={true}
      /> */}

      <div className="container">
        {status.message && (
          <div className={`status status-top ${status.type}`}>
            {status.message}
          </div>
        )}

        <header className="header">
          <div className="header-top">
            <Logo size="large" showText={true} />
            <LanguageSwitcher />
          </div>
          <div className="header-content">
            <h1>{t('app.title')}</h1>
            <p>{t('app.subtitle')}</p>
          </div>
          <div className="privacy-notice">
            <div className="privacy-icon">🔒</div>
            <div className="privacy-text">
              <strong>{t('privacy.title')}:</strong> {t('privacy.description')}
            </div>
          </div>
        </header>

        <div className="top-section">
          <div className="left-section">
            <FileUpload
              onFilesSelected={handleFilesSelected}
              fileInputRef={fileInputRef}
            />
          </div>

          <div className="right-section">
            <ConversionOptions
              options={options}
              onOptionsChange={handleOptionsChange}
            />
          </div>
        </div>

        {/* Mobile Ad - Using Auto Ads instead */}
        {/* <GoogleAds 
          adSlot="5534587888"
          className="mobile-ad"
          adFormat="auto"
          responsive={true}
        /> */}

        <div className="bottom-section">
          <ProgressBar
            isConverting={isConverting}
            progress={progress}
          />
          <FileList
            files={files}
            onRemoveFile={handleRemoveFile}
            onClearFiles={handleClearFiles}
            onConvert={handleConvert}
            isConverting={isConverting}
            progress={progress}
          />
        </div>

      </div>
      <Footer />
      <Analytics />
    </div>
  );
}

export default App;
